import { c as _c } from "react/compiler-runtime";
import { addDays, differenceInHours, differenceInMilliseconds } from 'date-fns';
import { useContext, useState } from 'react';
import { Route } from 'react-router';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { Request } from '../../../../../../../typings/Request.interface';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { capitalizeFirstLetter, extractFromArray } from '../../../../utils';
import { formatDate, getTimezoneAbbreviation } from '../../../../utils/date';
import { cause_filter_breakdown, getRejectionCauseBreakdown } from '../../../../utils/rejection-causes';
import Alert from '../../../common/base/Alert';
import Badge from '../../../common/base/Badge';
import Button, { ButtonGroup } from '../../../common/base/Button';
import Chip from '../../../common/base/Chip';
import Link from '../../../common/base/Link';
import Loading from '../../../common/base/Loading';
import Tabs from '../../../common/base/Tabs';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DisplayDate from '../../../common/DisplayDate';
import { Div } from '../../../common/helpers/StyledUtils';
import { MetadataGrid } from '../../../common/MetadataGrid';
import Table from '../../../common/Table/Table';
import { useToasts } from '../../../common/Toast';
import { GlobalContext } from '../../../contexts/GlobalContext';
import useRequestActions from '../../../hooks/useRequestActions';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewNavActions, StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavHeader, StyledViewNavSecondary, StyledViewSplitPaneContent, StyledViewSplitPaneLayout, StyledViewSplitPaneSidebar, StyledViewSplitPaneSidebarBody, ViewBreadcrumbsTitle, ViewMetadataCopyable, ViewMetadataDate, ViewMetadataItem, ViewSplitPaneContentHeader, ViewSplitPaneSidebarHeader } from '../../../layouts/ViewLayout';
import { DashboardContext } from '../DashboardContext';
import NotFound from '../NotFound';
import EntrySidebar from '../Requests/EntrySidebar';
import useRequestEntriesList from './useRequestEntriesList';
import Divider from '../../../common/base/Divider';
import useCopyAsCurl from '../../../hooks/useCopyAsCurl';
import { HeadersCollapsibleSection } from '../../../common/Data/Headers';
import { QueryCollapsibleSection } from '../../../common/Data/Query';
import { BodyCollapsibleSection } from '../../../common/Data/Body';
import { useCopyToClipboard } from '../../../../utils/copy';
import { APISource } from '../../../../../../../typings/Source.interface';
import { APISubscription } from '../../../../../../../typings/Subscription.interface';
import { APIOrganization } from '../../../../../../../typings/Organization.interface';
import useFiltersOffset from '../../../hooks/useFiltersOffset';
import { useDatePreferences } from '../../../hooks/useDatePreferences';
interface Query {
  selected_id?: string | string[];
  tab?: 'overview' | 'entries';
}
export const RequestAlerts = t0 => {
  const $ = _c(9);
  const {
    rejection_cause_breakdown,
    hours_before_archive,
    margin
  } = t0;
  let t1;
  if ($[0] !== margin || $[1] !== rejection_cause_breakdown) {
    t1 = rejection_cause_breakdown?.text && <Alert inline={true} m={margin} neutral={true} title={rejection_cause_breakdown.label}>{rejection_cause_breakdown?.text}</Alert>;
    $[0] = margin;
    $[1] = rejection_cause_breakdown;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== hours_before_archive || $[4] !== margin) {
    t2 = hours_before_archive <= 24 && hours_before_archive >= 0 && <Alert info={true} inline={true} m={margin} action={{
      label: "Upgrade",
      to: "/settings/organization/plans?highlight=retention_days",
      icon: "upgrade"
    }}>This request will be archived in{" "}{hours_before_archive === 0 ? "less then an hour" : `${hours_before_archive} hours`}.</Alert>;
    $[3] = hours_before_archive;
    $[4] = margin;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1 || $[7] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
export const RequestOverview = t0 => {
  const $ = _c(133);
  const {
    request,
    source,
    source_error,
    source_loading,
    rejection_cause_breakdown,
    subscription,
    organization,
    embedded: t1,
    embedded_metadata_title: t2,
    embedded_request_data_title: t3
  } = t0;
  const embedded = t1 === undefined ? false : t1;
  const embedded_metadata_title = t2 === undefined ? "Metadata" : t2;
  const embedded_request_data_title = t3 === undefined ? "HTTP Request" : t3;
  const {
    timezone
  } = useDatePreferences();
  const copyToClipboard = useCopyToClipboard();
  const {
    is_degraded_banner_active
  } = useContext(DashboardContext);
  let t4;
  if ($[0] !== request.ingested_at || $[1] !== subscription.retention_days) {
    const archived_at = addDays(new Date(request.ingested_at), subscription.retention_days);
    t4 = differenceInHours(archived_at, new Date());
    $[0] = request.ingested_at;
    $[1] = subscription.retention_days;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const hours_before_archive = t4;
  const request_url = `${source?.url}${request?.data?.path}`;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      columns: "130px 1fr",
      gap: 6
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const grid = t5;
  let t6;
  if ($[4] !== copyToClipboard || $[5] !== organization || $[6] !== request.data || $[7] !== request.id || $[8] !== request.retried || $[9] !== request_url || $[10] !== subscription.retention_days) {
    t6 = request.data ? <><Div flex={{
        align: "center",
        gap: 2
      }} m={{
        b: 4
      }}><Badge neutral={true}>{request.data.headers["x-hookdeck-original-method"] || "POST"}</Badge><Text mono={true} as="span" ellipsis={true} size="s">{request_url}</Text><Button ghost={true} icon="copy" muted={true} onClick={() => copyToClipboard(request_url)} /></Div><HeadersCollapsibleSection headers={request.data.headers} embedded={false} />{request.data.query && <QueryCollapsibleSection query={request.data.query} parsed_query={request.data.parsed_query} embedded={false} />}{(request.data.body || request.data.body_format === "binary") && <BodyCollapsibleSection body={request.data.body} body_format={request.data.body_format} headers={request.data.headers} embedded={false} type="request" id={request.id} />}</> : request.retried ? <Text as="p">This request has just been retried and is still being processed. Please check back in a few seconds.</Text> : <><Text as="p">This request is past your archival period of {subscription.retention_days} days, the request data is no longer available.</Text>{!organization && <Link to="/settings/organization/plans?highlight=retention_days" icon="upgrade">Upgrade Plan</Link>}</>;
    $[4] = copyToClipboard;
    $[5] = organization;
    $[6] = request.data;
    $[7] = request.id;
    $[8] = request.retried;
    $[9] = request_url;
    $[10] = subscription.retention_days;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const request_data_content = t6;
  if (!embedded) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ViewSplitPaneSidebarHeader title="Request details" />;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== hours_before_archive || $[14] !== rejection_cause_breakdown) {
      t8 = <RequestAlerts rejection_cause_breakdown={rejection_cause_breakdown} hours_before_archive={hours_before_archive} />;
      $[13] = hours_before_archive;
      $[14] = rejection_cause_breakdown;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== request.rejection_cause) {
      t9 = <ViewMetadataItem label="Status">{request.rejection_cause ? <Badge danger={true} subtle={true} icon="block">{request.rejection_cause}</Badge> : <Badge success={true} subtle={true} icon="check">Accepted</Badge>}</ViewMetadataItem>;
      $[16] = request.rejection_cause;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    let t10;
    if ($[18] !== request.id) {
      t10 = <ViewMetadataItem label="Request ID"><ViewMetadataCopyable value={request.id} /></ViewMetadataItem>;
      $[18] = request.id;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== source || $[21] !== source_error) {
      t11 = <ViewMetadataItem label="Source">{source_error ? source_error.response?.status === 410 ? <Text as="span" muted={true}>Deleted</Text> : <Text as="span" muted={true}>Error retrieving name</Text> : source ? <Chip outline={true} mono={true} label={source.name} to={`/sources/${source.id}`} /> : <Loading small={true} />}</ViewMetadataItem>;
      $[20] = source;
      $[21] = source_error;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    const t12 = request.verified;
    const t13 = !request.verified;
    const t14 = request.verified;
    const t15 = String(request.verified);
    let t16;
    if ($[23] !== t15) {
      t16 = capitalizeFirstLetter(t15);
      $[23] = t15;
      $[24] = t16;
    } else {
      t16 = $[24];
    }
    let t17;
    if ($[25] !== request.verified || $[26] !== t13 || $[27] !== t16) {
      t17 = <ViewMetadataItem label="Verified"><Badge subtle={t12} neutral={t13} success={t14}>{t16}</Badge></ViewMetadataItem>;
      $[25] = request.verified;
      $[26] = t13;
      $[27] = t16;
      $[28] = t17;
    } else {
      t17 = $[28];
    }
    let t18;
    if ($[29] !== request.ingested_at) {
      t18 = <ViewMetadataItem label="Received at"><ViewMetadataDate date={request.ingested_at} /></ViewMetadataItem>;
      $[29] = request.ingested_at;
      $[30] = t18;
    } else {
      t18 = $[30];
    }
    let t19;
    if ($[31] !== request.events_count) {
      t19 = <ViewMetadataItem label="Events created"><Badge neutral={true}>{request.events_count}</Badge></ViewMetadataItem>;
      $[31] = request.events_count;
      $[32] = t19;
    } else {
      t19 = $[32];
    }
    let t20;
    if ($[33] !== request.ignored_count) {
      t20 = <ViewMetadataItem label="Ignored events">{request.ignored_count && request.ignored_count > 0 ? <Badge warning={true} subtle={true}>{request.ignored_count}</Badge> : <Badge neutral={true}>{request.ignored_count ?? 0}</Badge>}</ViewMetadataItem>;
      $[33] = request.ignored_count;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    let t21;
    if ($[35] !== request.cli_events_count) {
      t21 = <ViewMetadataItem label="CLI events"><Badge neutral={true}>{request.cli_events_count}</Badge></ViewMetadataItem>;
      $[35] = request.cli_events_count;
      $[36] = t21;
    } else {
      t21 = $[36];
    }
    let t22;
    if ($[37] !== request.created_at || $[38] !== request.ingested_at) {
      t22 = differenceInMilliseconds(new Date(request.created_at), new Date(request.ingested_at));
      $[37] = request.created_at;
      $[38] = request.ingested_at;
      $[39] = t22;
    } else {
      t22 = $[39];
    }
    let t23;
    if ($[40] !== t22) {
      t23 = <ViewMetadataItem label="Ingestion latency"><Text>{t22}{" "}ms</Text></ViewMetadataItem>;
      $[40] = t22;
      $[41] = t23;
    } else {
      t23 = $[41];
    }
    let t24;
    if ($[42] !== t10 || $[43] !== t11 || $[44] !== t17 || $[45] !== t18 || $[46] !== t19 || $[47] !== t20 || $[48] !== t21 || $[49] !== t23 || $[50] !== t8 || $[51] !== t9) {
      t24 = <StyledViewSplitPaneSidebar><StyledViewSplitPaneSidebarBody>{t7}{t8}{t9}{t10}{t11}{t17}{t18}{t19}{t20}{t21}{t23}</StyledViewSplitPaneSidebarBody></StyledViewSplitPaneSidebar>;
      $[42] = t10;
      $[43] = t11;
      $[44] = t17;
      $[45] = t18;
      $[46] = t19;
      $[47] = t20;
      $[48] = t21;
      $[49] = t23;
      $[50] = t8;
      $[51] = t9;
      $[52] = t24;
    } else {
      t24 = $[52];
    }
    let t25;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <ViewSplitPaneContentHeader title="Request data" />;
      $[53] = t25;
    } else {
      t25 = $[53];
    }
    let t26;
    if ($[54] !== request_data_content) {
      t26 = <StyledViewSplitPaneContent>{t25}{request_data_content}</StyledViewSplitPaneContent>;
      $[54] = request_data_content;
      $[55] = t26;
    } else {
      t26 = $[55];
    }
    let t27;
    if ($[56] !== is_degraded_banner_active || $[57] !== t24 || $[58] !== t26) {
      t27 = <StyledViewSplitPaneLayout banner_active={is_degraded_banner_active}>{t24}{t26}</StyledViewSplitPaneLayout>;
      $[56] = is_degraded_banner_active;
      $[57] = t24;
      $[58] = t26;
      $[59] = t27;
    } else {
      t27 = $[59];
    }
    return t27;
  }
  let t7;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      t: 8,
      b: 4
    };
    $[60] = t7;
  } else {
    t7 = $[60];
  }
  let t8;
  if ($[61] !== embedded_metadata_title) {
    t8 = <Text bold={true} size="l" m={t7}>{embedded_metadata_title}</Text>;
    $[61] = embedded_metadata_title;
    $[62] = t8;
  } else {
    t8 = $[62];
  }
  let t9;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      columns: 2,
      gap: 3,
      gap_x: 10
    };
    $[63] = t9;
  } else {
    t9 = $[63];
  }
  let t10;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Text muted={true} ellipsis={true}>Status</Text>;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  let t11;
  if ($[65] !== request.rejection_cause) {
    t11 = <MetadataGrid grid={grid}>{t10}{request.rejection_cause ? <Badge danger={true} subtle={true} icon="block">{request.rejection_cause}</Badge> : <Badge success={true} subtle={true} icon="check">Accepted</Badge>}</MetadataGrid>;
    $[65] = request.rejection_cause;
    $[66] = t11;
  } else {
    t11 = $[66];
  }
  let t12;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Text muted={true} ellipsis={true}>Verified</Text>;
    $[67] = t12;
  } else {
    t12 = $[67];
  }
  const t13 = request.verified;
  const t14 = !request.verified;
  const t15 = request.verified;
  const t16 = String(request.verified);
  let t17;
  if ($[68] !== t16) {
    t17 = capitalizeFirstLetter(t16);
    $[68] = t16;
    $[69] = t17;
  } else {
    t17 = $[69];
  }
  let t18;
  if ($[70] !== request.verified || $[71] !== t14 || $[72] !== t17) {
    t18 = <MetadataGrid grid={grid}>{t12}<Badge subtle={t13} neutral={t14} success={t15}>{t17}</Badge></MetadataGrid>;
    $[70] = request.verified;
    $[71] = t14;
    $[72] = t17;
    $[73] = t18;
  } else {
    t18 = $[73];
  }
  let t19;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Text muted={true} ellipsis={true}>Source</Text>;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== source_error) {
    t20 = source_error && (source_error.response?.status === 410 ? <Text as="span" muted={true}>Deleted</Text> : <Text as="span" muted={true}>Error retrieving name</Text>);
    $[75] = source_error;
    $[76] = t20;
  } else {
    t20 = $[76];
  }
  let t21;
  if ($[77] !== source || $[78] !== source_error) {
    t21 = !source_error && source && <Chip outline={true} mono={true} label={source.name} to={`/sources/${source.id}`} />;
    $[77] = source;
    $[78] = source_error;
    $[79] = t21;
  } else {
    t21 = $[79];
  }
  let t22;
  if ($[80] !== source_loading) {
    t22 = source_loading && <Loading small={true} />;
    $[80] = source_loading;
    $[81] = t22;
  } else {
    t22 = $[81];
  }
  let t23;
  if ($[82] !== t20 || $[83] !== t21 || $[84] !== t22) {
    t23 = <MetadataGrid grid={grid}>{t19}<>{t20}{t21}{t22}</></MetadataGrid>;
    $[82] = t20;
    $[83] = t21;
    $[84] = t22;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Text muted={true} ellipsis={true}>Received at</Text>;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== request.ingested_at || $[88] !== timezone) {
    t25 = formatDate(request.ingested_at, "yyyy-MM-dd HH:mm:ss", timezone);
    $[87] = request.ingested_at;
    $[88] = timezone;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== request.ingested_at || $[91] !== timezone) {
    t26 = getTimezoneAbbreviation(timezone, request.ingested_at);
    $[90] = request.ingested_at;
    $[91] = timezone;
    $[92] = t26;
  } else {
    t26 = $[92];
  }
  let t27;
  if ($[93] !== t25 || $[94] !== t26) {
    t27 = <MetadataGrid grid={grid}>{t24}<Text mono={true}>{t25}{" "}{t26}</Text></MetadataGrid>;
    $[93] = t25;
    $[94] = t26;
    $[95] = t27;
  } else {
    t27 = $[95];
  }
  let t28;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Text muted={true} ellipsis={true}>Created events</Text>;
    $[96] = t28;
  } else {
    t28 = $[96];
  }
  let t29;
  if ($[97] !== request.events_count) {
    t29 = <MetadataGrid grid={grid}>{t28}<Badge neutral={true}>{request.events_count}</Badge></MetadataGrid>;
    $[97] = request.events_count;
    $[98] = t29;
  } else {
    t29 = $[98];
  }
  let t30;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Text muted={true} ellipsis={true}>Ignored events</Text>;
    $[99] = t30;
  } else {
    t30 = $[99];
  }
  let t31;
  if ($[100] !== request.ignored_count) {
    t31 = <MetadataGrid grid={grid}>{t30}{request.ignored_count && request.ignored_count > 0 ? <Badge warning={true} subtle={true}>{request.ignored_count}</Badge> : <Badge neutral={true}>{request.ignored_count ?? 0}</Badge>}</MetadataGrid>;
    $[100] = request.ignored_count;
    $[101] = t31;
  } else {
    t31 = $[101];
  }
  let t32;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <Text muted={true} ellipsis={true}>Created CLI events</Text>;
    $[102] = t32;
  } else {
    t32 = $[102];
  }
  let t33;
  if ($[103] !== request.cli_events_count) {
    t33 = <MetadataGrid grid={grid}>{t32}<Badge neutral={true}>{request.cli_events_count}</Badge></MetadataGrid>;
    $[103] = request.cli_events_count;
    $[104] = t33;
  } else {
    t33 = $[104];
  }
  let t34;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <Text muted={true} ellipsis={true}>Ingestion latency</Text>;
    $[105] = t34;
  } else {
    t34 = $[105];
  }
  let t35;
  if ($[106] !== request.created_at || $[107] !== request.ingested_at) {
    t35 = differenceInMilliseconds(new Date(request.created_at), new Date(request.ingested_at));
    $[106] = request.created_at;
    $[107] = request.ingested_at;
    $[108] = t35;
  } else {
    t35 = $[108];
  }
  let t36;
  if ($[109] !== t35) {
    t36 = <MetadataGrid grid={grid}>{t34}<Text>{t35}{" "}ms</Text></MetadataGrid>;
    $[109] = t35;
    $[110] = t36;
  } else {
    t36 = $[110];
  }
  let t37;
  if ($[111] !== t11 || $[112] !== t18 || $[113] !== t23 || $[114] !== t27 || $[115] !== t29 || $[116] !== t31 || $[117] !== t33 || $[118] !== t36) {
    t37 = <Div grid={t9}>{t11}{t18}{t23}{t27}{t29}{t31}{t33}{t36}</Div>;
    $[111] = t11;
    $[112] = t18;
    $[113] = t23;
    $[114] = t27;
    $[115] = t29;
    $[116] = t31;
    $[117] = t33;
    $[118] = t36;
    $[119] = t37;
  } else {
    t37 = $[119];
  }
  let t38;
  if ($[120] !== t37 || $[121] !== t8) {
    t38 = <Div>{t8}{t37}</Div>;
    $[120] = t37;
    $[121] = t8;
    $[122] = t38;
  } else {
    t38 = $[122];
  }
  let t39;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <Div p={{
      x: 0,
      t: 10,
      b: 8
    }}><Divider /></Div>;
    $[123] = t39;
  } else {
    t39 = $[123];
  }
  let t40;
  if ($[124] !== embedded_request_data_title || $[125] !== request.data) {
    t40 = request.data && <Text bold={true} size="l" m={{
      t: 0,
      b: 4
    }}>{embedded_request_data_title}</Text>;
    $[124] = embedded_request_data_title;
    $[125] = request.data;
    $[126] = t40;
  } else {
    t40 = $[126];
  }
  let t41;
  if ($[127] !== request_data_content || $[128] !== t40) {
    t41 = <Div>{t40}{request_data_content}</Div>;
    $[127] = request_data_content;
    $[128] = t40;
    $[129] = t41;
  } else {
    t41 = $[129];
  }
  let t42;
  if ($[130] !== t38 || $[131] !== t41) {
    t42 = <>{t38}{t39}{t41}</>;
    $[130] = t38;
    $[131] = t41;
    $[132] = t42;
  } else {
    t42 = $[132];
  }
  return t42;
};
const RequestEntries = t0 => {
  const $ = _c(23);
  const {
    entries,
    count,
    has_next,
    has_prev,
    next,
    prev,
    fetched,
    onSelected,
    handleRetry,
    selected_id,
    webhooks_by_id,
    request
  } = t0;
  const {
    top_offset
  } = useFiltersOffset(78);
  let t1;
  if ($[0] !== entries || $[1] !== handleRetry || $[2] !== request || $[3] !== selected_id || $[4] !== webhooks_by_id) {
    let t2;
    if ($[6] !== handleRetry || $[7] !== request || $[8] !== selected_id || $[9] !== webhooks_by_id) {
      t2 = entry => ({
        id: entry.id,
        selected: selected_id === entry.id,
        fields: [<DisplayDate key={entry.id} date={entry.created_at || request.created_at} show_timezone={false} />, "cause" in entry ? <Div key={entry.id} flex={{
          justify: "space-between",
          gap: 2,
          align: "center"
        }}><Badge icon="block" neutral={true} mono={true}>{cause_filter_breakdown[entry.cause](entry).label}</Badge><Button.Permission role="member" ghost={true} icon="retry" muted={true} small={true} onClick={e => {
            e.stopPropagation();
            handleRetry(entry.webhook_id);
          }} /></Div> : <Badge icon="success" neutral={true} mono={true} key={entry.id}>Created Event</Badge>, !webhooks_by_id?.[entry.webhook_id] ? <Text size="s" muted={true}>Connection Deleted</Text> : <Chip outline={true} label={webhooks_by_id?.[entry.webhook_id]?.full_name} mono={true} to={`/connections/${entry.webhook_id}`} />]
      });
      $[6] = handleRetry;
      $[7] = request;
      $[8] = selected_id;
      $[9] = webhooks_by_id;
      $[10] = t2;
    } else {
      t2 = $[10];
    }
    t1 = entries.map(t2);
    $[0] = entries;
    $[1] = handleRetry;
    $[2] = request;
    $[3] = selected_id;
    $[4] = webhooks_by_id;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const rows = t1;
  let t2;
  let t3;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["Entry Date", "Status", "Connection"];
    t3 = [{
      min: 150,
      max: 150
    }, {
      min: 222,
      max: 222
    }, {
      min: 100
    }];
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  const t4 = !fetched;
  const t5 = has_next ? next : undefined;
  const t6 = has_prev ? prev : undefined;
  let t7;
  if ($[13] !== onSelected) {
    t7 = row_id => onSelected(row_id || null);
    $[13] = onSelected;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== count || $[16] !== rows || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== top_offset) {
    t8 = <Table headers={t2} widths={t3} current_count={count} loading={t4} fill={true} top_offset={top_offset} onNextPage={t5} onPreviousPage={t6} onRowSelected={t7} rows={rows} />;
    $[15] = count;
    $[16] = rows;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = top_offset;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
const RequestView: React.FC = () => {
  const $ = _c(115);
  const {
    id: request_id
  } = useParams();
  const [retrying, setRetrying] = useState(false);
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  let t0;
  if ($[0] !== query.selected_id) {
    t0 = extractFromArray(query.selected_id);
    $[0] = query.selected_id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const selected_id = t0 as string;
  const is_overview_tab = location.pathname.split("/").length === 3;
  const active_tab = is_overview_tab ? "overview" : "entries";
  let t1;
  if ($[2] !== updateSearchQuery) {
    t1 = selected_id_0 => updateSearchQuery({
      selected_id: selected_id_0 || undefined
    });
    $[2] = updateSearchQuery;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onSelected = t1;
  const {
    addToast
  } = useToasts();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    subscription,
    organization
  } = useContext(DashboardContext);
  const {
    openActionDialog
  } = useRequestActions();
  let t2;
  if ($[4] !== request_id) {
    t2 = APIMethodKeys.requests.get(request_id);
    $[4] = request_id;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== HookdeckAPI.requests || $[7] !== request_id) {
    t3 = () => HookdeckAPI.requests.get(request_id) as Promise<Request & {
      retried?: boolean;
    }>;
    $[6] = HookdeckAPI.requests;
    $[7] = request_id;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    data: request,
    error,
    isValidating,
    mutate
  } = useSWR(t2, t3);
  const {
    entries,
    fetched,
    count,
    has_next,
    has_prev,
    next,
    prev,
    revalidate
  } = useRequestEntriesList(request, 50);
  let t4;
  if ($[9] !== HookdeckAPI.requests || $[10] !== addToast || $[11] !== mutate || $[12] !== onSelected || $[13] !== request_id || $[14] !== retrying || $[15] !== revalidate || $[16] !== selected_id || $[17] !== updateSearchQuery) {
    t4 = webhook_id => {
      if (retrying) {
        return;
      }
      setRetrying(true);
      HookdeckAPI.requests.retry(request_id, webhook_id ? [webhook_id] : undefined).then(t5 => {
        const {
          request: request_0,
          events
        } = t5;
        addToast("success", `Request has been retried and ${events.length} events were created.`);
        mutate({
          ...request_0,
          retried: true
        }, false);
        revalidate(events);
        if (events) {
          const event = events.find(e => e.webhook_id === webhook_id);
          if (selected_id && event && selected_id !== event.id) {
            onSelected(event.id);
          } else {
            if (!selected_id && events.length > 0 && active_tab !== "entries") {
              updateSearchQuery({
                tab: "entries"
              });
            }
          }
        }
      }).catch(() => {
        addToast("error", "Request could not be retried");
      }).finally(() => {
        setRetrying(false);
      });
    };
    $[9] = HookdeckAPI.requests;
    $[10] = addToast;
    $[11] = mutate;
    $[12] = onSelected;
    $[13] = request_id;
    $[14] = retrying;
    $[15] = revalidate;
    $[16] = selected_id;
    $[17] = updateSearchQuery;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const handleRetry = t4;
  let t5;
  let t6;
  let unique_webhook_ids;
  if ($[19] !== entries) {
    unique_webhook_ids = entries ? Array.from(new Set(entries.map(_temp))).sort() : [];
    t5 = useSWR;
    t6 = unique_webhook_ids.length > 0 && APIMethodKeys.webhooks.list({
      id: unique_webhook_ids
    });
    $[19] = entries;
    $[20] = t5;
    $[21] = t6;
    $[22] = unique_webhook_ids;
  } else {
    t5 = $[20];
    t6 = $[21];
    unique_webhook_ids = $[22];
  }
  let t7;
  if ($[23] !== HookdeckAPI.webhooks || $[24] !== unique_webhook_ids) {
    t7 = () => HookdeckAPI.webhooks.list({
      id: unique_webhook_ids
    });
    $[23] = HookdeckAPI.webhooks;
    $[24] = unique_webhook_ids;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const {
    data: webhooks
  } = t5(t6, t7);
  let t8;
  if ($[26] !== webhooks) {
    t8 = webhooks && webhooks.models.reduce(_temp2, {});
    $[26] = webhooks;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const webhooks_by_id = t8;
  let t9;
  if ($[28] !== request) {
    t9 = request && APIMethodKeys.sources.get(request.source_id);
    $[28] = request;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== HookdeckAPI.sources || $[31] !== request?.source_id) {
    t10 = () => HookdeckAPI.sources.get(request?.source_id as string);
    $[30] = HookdeckAPI.sources;
    $[31] = request?.source_id;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const {
    data: source,
    error: source_error,
    isLoading: source_loading
  } = useSWR(t9, t10);
  let t11;
  if ($[33] !== request || $[34] !== source) {
    t11 = {
      request,
      source
    };
    $[33] = request;
    $[34] = source;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const copyAsCurl = useCopyAsCurl(t11);
  let t12;
  if ($[36] !== request || $[37] !== source) {
    t12 = request && source && request.rejection_cause && getRejectionCauseBreakdown(request.rejection_cause)({
      request,
      source
    });
    $[36] = request;
    $[37] = source;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const rejection_cause_breakdown = t12;
  const t13 = `/requests/${request_id}`;
  let t14;
  if ($[39] !== t13) {
    t14 = {
      label: "Overview",
      key: "overview",
      to: t13
    };
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const t15 = `/requests/${request_id}/entries`;
  let t16;
  if ($[41] !== count || $[42] !== t15) {
    t16 = {
      label: "Entries",
      key: "entries",
      to: t15,
      badge_theme: "muted",
      badge_label: count
    };
    $[41] = count;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t14 || $[45] !== t16) {
    t17 = {
      OVERVIEW: t14,
      ENTRIES: t16
    };
    $[44] = t14;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  const TABS = t17 as const;
  let t18;
  if ($[47] !== updateSearchQuery) {
    t18 = tabKey => {
      updateSearchQuery({
        tab: tabKey
      }, {
        replace: true
      });
    };
    $[47] = updateSearchQuery;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  const handleTabSelected = t18;
  if (error && error.response?.status === 404) {
    const t19 = `Requests can take a moment to become available in the dashboard. Alternatively if the request is older then ${subscription.retention_days} days it's been archived.`;
    let t20;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = {
        to: "/requests",
        text: "All Requests"
      };
      $[49] = t20;
    } else {
      t20 = $[49];
    }
    const t21 = isValidating ? "loading" : "refresh";
    let t22;
    if ($[50] !== mutate) {
      t22 = () => mutate();
      $[50] = mutate;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    let t23;
    if ($[52] !== isValidating || $[53] !== t21 || $[54] !== t22) {
      t23 = <Button icon={t21} disabled={isValidating} ghost={true} onClick={t22}>Check again</Button>;
      $[52] = isValidating;
      $[53] = t21;
      $[54] = t22;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    let t24;
    if ($[56] !== request_id || $[57] !== t19 || $[58] !== t23) {
      t24 = <NotFound title="Request not available or archived" description={t19} id={request_id} link={t20} extra_action={t23} />;
      $[56] = request_id;
      $[57] = t19;
      $[58] = t23;
      $[59] = t24;
    } else {
      t24 = $[59];
    }
    return t24;
  }
  const loading = !request || source_loading || unique_webhook_ids.length > 0 && webhooks === undefined;
  if (loading) {
    let t19;
    if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = {
        justify: "center"
      };
      $[60] = t19;
    } else {
      t19 = $[60];
    }
    let t20;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t20 = <Div flex={t19} p={8}><Loading /></Div>;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    return t20;
  }
  let t19;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      label: "Requests",
      to: "/requests"
    };
    $[62] = t19;
  } else {
    t19 = $[62];
  }
  let t20;
  if ($[63] !== request_id) {
    t20 = <ViewBreadcrumbsTitle breadcrumbs={[t19, {
      label: request_id,
      highlight: true
    }]} />;
    $[63] = request_id;
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  let t21;
  if ($[65] !== copyAsCurl || $[66] !== handleRetry || $[67] !== openActionDialog || $[68] !== request || $[69] !== retrying) {
    t21 = active_tab === "overview" && <StyledViewNavActions><Tooltip tooltip="Copy request as cURL" side="bottom"><Button outline={true} icon="copy" onClick={copyAsCurl}>cURL</Button></Tooltip><ButtonGroup><Tooltip side="bottom" tooltip={request.rejection_cause ? "Re-process this rejected request to create new events on its connections." : "Accepted requests cannot be retried"}><Button.Permission role="member" disabled={retrying || !request.rejection_cause} onClick={() => handleRetry()} outline={true} icon={retrying ? "loading" : "retry"}>Retry</Button.Permission></Tooltip><Tooltip side="bottom" tooltip="Re-ingest this request through the pipeline as a brand-new request, targeting a chosen set of connections."><Button.Permission role="member" outline={true} icon="replay" disabled={retrying} onClick={() => request && openActionDialog(request)}>Replay</Button.Permission></Tooltip></ButtonGroup></StyledViewNavActions>;
    $[65] = copyAsCurl;
    $[66] = handleRetry;
    $[67] = openActionDialog;
    $[68] = request;
    $[69] = retrying;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== t20 || $[72] !== t21) {
    t22 = <StyledViewNavHeader>{t20}{t21}</StyledViewNavHeader>;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== TABS) {
    t23 = Object.values(TABS);
    $[74] = TABS;
    $[75] = t23;
  } else {
    t23 = $[75];
  }
  let t24;
  if ($[76] !== handleTabSelected || $[77] !== t23) {
    t24 = <StyledViewNavSecondary><Tabs active_tab={active_tab} onTabSelected={handleTabSelected} border={false} tabs={t23} /></StyledViewNavSecondary>;
    $[76] = handleTabSelected;
    $[77] = t23;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== t22 || $[80] !== t24) {
    t25 = <StyledViewNav>{t22}{t24}</StyledViewNav>;
    $[79] = t22;
    $[80] = t24;
    $[81] = t25;
  } else {
    t25 = $[81];
  }
  const t26 = rejection_cause_breakdown || null;
  let t27;
  if ($[82] !== organization || $[83] !== request || $[84] !== source || $[85] !== source_error || $[86] !== source_loading || $[87] !== subscription || $[88] !== t26) {
    t27 = <Route exact={true} path="/requests/:id"><RequestOverview request={request} source={source} source_error={source_error} source_loading={source_loading} rejection_cause_breakdown={t26} subscription={subscription} organization={organization} /></Route>;
    $[82] = organization;
    $[83] = request;
    $[84] = source;
    $[85] = source_error;
    $[86] = source_loading;
    $[87] = subscription;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  let t28;
  if ($[90] !== count || $[91] !== entries || $[92] !== fetched || $[93] !== handleRetry || $[94] !== has_next || $[95] !== has_prev || $[96] !== next || $[97] !== onSelected || $[98] !== prev || $[99] !== request || $[100] !== selected_id || $[101] !== webhooks_by_id) {
    t28 = <Route exact={true} path="/requests/:id/entries"><RequestEntries entries={entries} count={count} has_next={has_next} has_prev={has_prev} next={next} prev={prev} fetched={fetched} onSelected={onSelected} handleRetry={handleRetry} selected_id={selected_id} webhooks_by_id={webhooks_by_id} request={request} /></Route>;
    $[90] = count;
    $[91] = entries;
    $[92] = fetched;
    $[93] = handleRetry;
    $[94] = has_next;
    $[95] = has_prev;
    $[96] = next;
    $[97] = onSelected;
    $[98] = prev;
    $[99] = request;
    $[100] = selected_id;
    $[101] = webhooks_by_id;
    $[102] = t28;
  } else {
    t28 = $[102];
  }
  let t29;
  if ($[103] !== t25 || $[104] !== t27 || $[105] !== t28) {
    t29 = <StyledViewContent>{t25}{t27}{t28}</StyledViewContent>;
    $[103] = t25;
    $[104] = t27;
    $[105] = t28;
    $[106] = t29;
  } else {
    t29 = $[106];
  }
  let t30;
  if ($[107] !== handleRetry || $[108] !== onSelected || $[109] !== request_id || $[110] !== selected_id) {
    t30 = selected_id && <EntrySidebar request_id={request_id} entry_id={selected_id} onClose={() => onSelected(null)} retry={(request_1, webhook_ids) => handleRetry(webhook_ids[0])} />;
    $[107] = handleRetry;
    $[108] = onSelected;
    $[109] = request_id;
    $[110] = selected_id;
    $[111] = t30;
  } else {
    t30 = $[111];
  }
  let t31;
  if ($[112] !== t29 || $[113] !== t30) {
    t31 = <StyledViewLayout>{t29}{t30}</StyledViewLayout>;
    $[112] = t29;
    $[113] = t30;
    $[114] = t31;
  } else {
    t31 = $[114];
  }
  return t31;
};
export default RequestView;
function _temp(entry) {
  return entry.webhook_id;
}
function _temp2(object, webhook) {
  return {
    ...object,
    [webhook.id]: webhook
  };
}